import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IIsThisYou } from "./IIsThisYou";
import { useCompanyData } from "../../context/CompanyDataContext";
import { useTraineeData } from "../../context/TraineeDataContext";
import { fetchTrainee } from "../../helpers/getTraineeVersion";
import { getTrainee } from "../../graphql/queries";
import sendWebSocketMessage from "../../services/WebSocket/sendWebSocketMessage/SendWebSocketMessage";

import { API } from "aws-amplify";
import { v4 as uuid } from "uuid";

import {
	createCheckIn,
	updateTrainee,
	createTrainee,
} from "../../graphql/mutations";

const IsThisYou: React.FC<IIsThisYou> = ({ data, httpService }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [firstName, setFirstName] = useState<string>(data.first_name);
	const [lastName, setLastName] = useState<string>(data.last_name);
	const [phone, setPhone] = useState<string>(data.phone);
	const [disabled, setDisabled] = useState<boolean>(true);

	const onChangeCheckBox = () => {
		setDisabled(!disabled);
	};

	const {
		companyId: companyIdParam = "",
		regionId: regionIdParam = "",
		locationId: locationIdParam = "",
	} = useParams<{ companyId: string; regionId: string; locationId: string }>();

	const { companyData, setCompanyData } = useCompanyData();
	const { traineeData } = useTraineeData();
	const canSubmit =
		(data.checked_in || !disabled) && !isSubmitting && Boolean(companyData);

	const navigate = useNavigate();

	useEffect(() => {
		[
			`checkedIn-${locationIdParam}`,
			`locationId-${locationIdParam}`,
			`outOfDate-${locationIdParam}`,
			`traineeData-${locationIdParam}`,
			`companyData-${locationIdParam}`,
		].forEach((key) => localStorage.removeItem(key));
	}, []);

	useEffect(() => {
		if (!companyData && locationIdParam) {
			httpService
				.getData(`location/id/${locationIdParam}`)
				.then((responseData) => {
					setCompanyData(responseData);
				})
				.catch((error) => {
					console.error("Failed to pre-load company data:", error);
				});
		}
	}, [companyData, httpService, locationIdParam, setCompanyData]);

	const handleCheckInClick = () => {
		navigate(`/checkin/${companyIdParam}/${regionIdParam}/${locationIdParam}`);
	};

	const getStatus = (dateCompleted: number) => {
		const currentDate = new Date();

		const trainingDate = new Date(
			dateCompleted < 10000000000 ? dateCompleted * 1000 : dateCompleted,
		);

		if (isNaN(trainingDate.getTime())) {
			return "invalid date";
		}

		if (companyData?.expiration_time === "calendar") {
			const expirationDate = new Date(trainingDate.getFullYear(), 11, 31);
			return expirationDate >= currentDate ? "active" : "expired";
		} else {
			const expirationDate = new Date(trainingDate);
			expirationDate.setDate(expirationDate.getDate() + 365);
			return expirationDate >= currentDate ? "active" : "expired";
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (isSubmitting) return;
		setIsSubmitting(true);

		if (getStatus(data.last_training) !== "active") {
			navigate(
				`/training-out-of-date/${companyIdParam}/${regionIdParam}/${locationIdParam}`,
			);
			return;
		}

		try {
			const checkinId = uuid();
			const checkInInput = {
				id: checkinId,
				trainee_id: traineeData.id,
				location_id: locationIdParam,
				timestamp: Math.floor(Date.now() / 10000),
				type: 1,
				trainee_name: traineeData.display_name,
				trainee_phone: traineeData.phone,
				trainee_company: traineeData.display_company,
				visitor_type: traineeData.visitor_type,
				display_company: traineeData.display_company,
			};
			const checkinObject = {
				id: checkinId,
				trainee_id: traineeData.id,
				location_id: locationIdParam,
				type: 1,
				timestamp: Date.now(),
				company_id: companyIdParam,
				region_id: regionIdParam,
			};

			let trainee = await httpService.getData(`trainee/id/${data.id}`);

			if (trainee.checked_in === false) {
				await httpService
					.postData(`checkin`, checkinObject)
					.then((responseData) => {})
					.catch((error) => {
						console.error("Error during check-in:", error);
					});

				sendWebSocketMessage(
					`locationId=${locationIdParam}`,
					`${firstName} ${lastName} checked in at ${companyData?.name ?? ""}`,
					"sendmessage",
				);

				await API.graphql({
					query: createCheckIn,
					variables: { input: checkInInput },
					authMode: "AWS_IAM",
				});

				const existing = await fetchTrainee(traineeData.id);

				let nextVersion: number;

				if (!existing) {
					const { data }: any = await API.graphql({
						query: createTrainee,
						variables: {
							input: {
								id: traineeData.id,
								first_name: firstName,
								last_name: lastName,
								display_name: traineeData.display_name,
								visitor_type: traineeData.visitor_type,
								display_company: traineeData.display_company,
								phone: traineeData.phone,
								location_id: locationIdParam,
								checked_in: true,
								onsite_pk: locationIdParam,
							},
						},
						authMode: "AWS_IAM",
					});
					nextVersion = data.createTrainee._version;
				} else {
					nextVersion = existing._version;
				}

				await API.graphql({
					query: updateTrainee,
					variables: {
						input: {
							id: traineeData.id,
							checked_in: true,
							onsite_pk: locationIdParam,
							visitor_type: traineeData.visitor_type,
							display_company: traineeData.display_company,
							timestamp: Math.floor(Date.now() / 1000),
							_version: nextVersion,
						},
					},
					authMode: "AWS_IAM",
				});

				navigate(
					`/training-verified/${companyIdParam}/${regionIdParam}/${locationIdParam}`,
				);
			} else {
				navigate(
					`/training-verified/${companyIdParam}/${regionIdParam}/${locationIdParam}`,
				);
			}
		} catch (err) {
			setIsSubmitting(false);
			navigate(
				`/training-verified/${companyIdParam}/${regionIdParam}/${locationIdParam}`,
			);
		}
	};

	return (
		<div className="tervuren-container__column-center bg-primary mh-100 flex-gap-1">
			<div className="tervuren-container__column-center-75">
				<i className="trainee-profile-icon" style={{ color: "#FFF" }} />
				<h1 className="text-center fw-500">Is this you?</h1>
				<p className="text-center mb-2">
					We found the following record. Please confirm this is you.
				</p>
				<form
					onSubmit={handleSubmit}
					className="tervuren-container__column-center width-100 tervuren-container__column-center"
				>
					<div className="is-this-you-form_label-and-input-wrapper">
						<label htmlFor="firstName" className="fw-400">
							First Name:
						</label>
						<input
							type="text"
							name="firstName"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							placeholder="First Name"
						/>
					</div>
					<div className="is-this-you-form_label-and-input-wrapper">
						<label htmlFor="lastName" className="fw-400">
							Last Name:
						</label>
						<input
							type="text"
							name="lastName"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							placeholder="Last Name"
						/>
					</div>
					<div className="is-this-you-form_label-and-input-wrapper">
						<label htmlFor="phone" className="fw-400">
							Phone:
						</label>
						<input
							type="text"
							name="phone"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							placeholder="Phone"
							className="mb-2"
						/>
					</div>

					{!data.checked_in && (
						<div className="is-this-you-form_label-and-input-wrapper flex">
							<label
								htmlFor="confirm"
								className="flex"
								style={{ width: "30rem", maxWidth: "fit-content" }}
							>
								<input
									style={{ marginRight: "10px" }}
									type="checkbox"
									id="confirm"
									name="confirm"
									onChange={onChangeCheckBox}
									key={"2"}
								/>
								By checking this box, you explicitly agree to receive safety and
								general info text messages while on site. This opt-in is
								required to receive any messages.
							</label>
						</div>
					)}

					<button
						disabled={!canSubmit}
						className={`tervuren-button-primary mb-1 mt-8 ${
							!canSubmit ? "disabled" : ""
						}`}
						type="submit"
					>
						{isSubmitting ? "Processing..." : "Yes"}
					</button>
					<button
						onClick={handleCheckInClick}
						className="tervuren-button-secondary mb-1"
					>
						No
					</button>
				</form>
			</div>
		</div>
	);
};

export default IsThisYou;
